import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useNavigate } from 'react-router-dom';

import Button from '@amzn/meridian/button';
import Divider from '@amzn/meridian/divider';
import Row from '@amzn/meridian/row';
import LoadingSpinnerWrapper from 'src/components/shared/loadingSpinner/loadingSpinnerWrapper';
import { TOAST_TIMEOUT } from 'src/config/Toast';
import type { RegionTask, RegionTaskModification } from 'src/services/tasks/types';
import { AdminConfigPaths } from 'src/store/adminConfigPageSlice/common/constants';
import {
  changeTasksOrder,
  fetchRegionGroupsAndTasksByRegionId,
  getTableRowModifications,
  resetSeletedRegion,
  setFormTasksTableData,
  setSelectedFormTasksTableRow,
  setSelectedRegion,
  setTableRowModifications,
  updateRegionTasks,
  updateRestrictionForm,
} from 'src/store/adminConfigPageSlice/regionTasksPageSlice/regionTasksPageSlice';
import { getModifications } from 'src/store/modificationsSlice';
import { useAppDispatch, useAppSelector } from 'src/store/store';
import { createToast } from 'src/store/toastsSlice';

import AssignModificationsModal, { type ModificationOption } from './assignModificationsModal';
import { ROW_HEIGHT } from './constants';
import FormTaskBody from './formTaskBody';
import './formTaskConfiguration.scss';

import FormTasksTable from './formTasksTable/formTasksTable';

// **** Components **** //

/**
 * Default component
 */
const FormTaskConfiguration = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { userLoading } = useAppSelector((state) => state.user);
  const allModificationRecords = useAppSelector((state) => state.modifications.modifications);
  const { selectedRegion, taskFormTableData, selectedTaskFormTableRow } = useAppSelector(
    (state) => state.regionTasksConfigPage,
  );
  const [showSpinner, setShowSpinner] = useState(false);

  // ------------------------------ onLoad --------------------------------- //

  // Fetch IO data when page loads
  useEffect(() => {
    const fetchIOData = async () => {
      setShowSpinner(true);
      try {
        await Promise.allSettled([
          dispatch(fetchRegionGroupsAndTasksByRegionId(selectedRegion.id)),
          dispatch(getModifications()),
        ]);
      } finally {
        setShowSpinner(false);
      }
    };
    if (userLoading === 'fulfilled' && selectedRegion.id) {
      fetchIOData();
    }
  }, [userLoading, selectedRegion.id]);

  // ----------------------- AssignModifications Modal --------------------- //

  // Get a modification record by its id
  const { getModificationRecord, modificationOptions } = useMemo(() => {
    const arr: [number, RegionTaskModification][] = allModificationRecords.map((m) => [
      m.id,
      { ...m, modificationId: m.id },
    ]);
    const map = new Map(arr);
    const getFn = (id: number): RegionTaskModification => {
      const record = map.get(id);
      if (!record) throw new Error(`Modification with id ${id} not found.`);
      return record;
    };
    return {
      getModificationRecord: getFn,
      modificationOptions: allModificationRecords.map((m) => ({ value: m.id, label: m.name })),
    };
  }, [allModificationRecords]);

  // Currently selected modification options
  const currentModificationOptions: ModificationOption[] = useMemo(() => {
    const modifications = getTableRowModifications(selectedTaskFormTableRow);
    return modifications.map((m) => ({ value: m.modificationId, label: m.name }));
  }, [selectedTaskFormTableRow]);

  // Update the list of modifications for a task/group.
  const updateTaskModifications = useCallback(
    (newlySelectedOptions: ModificationOption[]) => {
      if (!selectedTaskFormTableRow) return;
      const modifications = newlySelectedOptions.map((o) => getModificationRecord(o.value));
      const toUpdate = setTableRowModifications(selectedTaskFormTableRow, modifications);
      const updatedTableData = taskFormTableData.map((r) => (r.entityId === toUpdate.entityId ? toUpdate : r));
      dispatch(setFormTasksTableData(updatedTableData));
      dispatch(setSelectedFormTasksTableRow(null));
    },
    [selectedTaskFormTableRow, taskFormTableData],
  );

  // ------------------------- Form Table --------------------------------- //

  // Change the order of the tasks (via drag-n-drop)
  const updateDataRowOrder = (oldIndex: number, newIndex: number) => {
    if (String(oldIndex) !== String(newIndex)) {
      dispatch(changeTasksOrder({ old: oldIndex, new: newIndex }));
    }
  };

  // ------------------------- Save/Cancel --------------------------------- //

  const onCancel = () => {
    dispatch(resetSeletedRegion());
    navigate(AdminConfigPaths.Form.TABLE, { replace: true });
  };

  const onSave = async () => {
    setShowSpinner(true);
    // Set the rank for the child-tasks to the rank in the parent
    const tasksToUpdate: RegionTask[] = taskFormTableData.flatMap((row) => {
      if (row.type === 'task-group') {
        return row.tasks.map((childTask) => ({
          ...childTask,
          rank: row.rank,
          modifications: [...row.commonModifications],
        }));
      } else {
        return row;
      }
    });
    // Call APIs
    try {
      await Promise.allSettled([
        dispatch(updateRestrictionForm(selectedRegion)),
        dispatch(updateRegionTasks(tasksToUpdate)),
      ]);
      dispatch(
        createToast({
          type: 'success',
          message: 'Updated region tasks and/or taskGroups successfully',
          timeout: TOAST_TIMEOUT,
        }),
      );
    } finally {
      setShowSpinner(false);
    }
  };

  // -------------------------- Render DOM --------------------------------- //

  return (
    <div data-testid="form-task-config-panel" className="view-and-edit-forms">
      {/* Form */}
      <LoadingSpinnerWrapper showSpinner={showSpinner} centerOfScreen={true}>
        <Row widths={['grid-11', 'grid-1']} height={ROW_HEIGHT}>
          <Row spacingInset="none none none 300">
            <Button size="small" type="secondary" onClick={onCancel}>
              Cancel
            </Button>
          </Row>
          <Row alignmentHorizontal="end" spacingInset="none 300 none none">
            <Button size="small" onClick={onSave}>
              Save
            </Button>
          </Row>
        </Row>
        <Divider spacingAfter="500" />
        <FormTaskBody region={selectedRegion} onUpdate={(region) => dispatch(setSelectedRegion(region))} />
        <Divider spacingBefore="500" spacingAfter="450" />
        <FormTasksTable
          dataRows={taskFormTableData}
          updateDataRowOrder={updateDataRowOrder}
          onClickEdit={(index) => dispatch(setSelectedFormTasksTableRow(taskFormTableData[index]))}
        />
      </LoadingSpinnerWrapper>

      {/* Modals */}
      <AssignModificationsModal
        open={!!selectedTaskFormTableRow}
        onClose={() => dispatch(setSelectedFormTasksTableRow(null))}
        selectedTaskLabel={selectedTaskFormTableRow?.label}
        options={modificationOptions}
        currentOptions={currentModificationOptions}
        onSubmit={updateTaskModifications}
      />
    </div>
  );
};

// **** Export **** //

export default FormTaskConfiguration;
